import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPinIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { elementoService, Elemento } from '../services/elementoService';
import DataTable, { FilterConfig, ServerPageBar } from '../components/DataTable';
import Notification from '../components/Notification';
import ViewToggle from '../components/ViewToggle';
import { useClienteSucursalFilter } from '../hooks/useClienteSucursalFilter';
import { useAbortableLoad } from '../hooks/useAbortableLoad';
import { useListUrlState } from '../hooks/useListUrlState';
import { isAbortError, LIST_PAGE_SIZE, hasClienteFilter, toListQuery } from '../utils/listSearch';
import { useCenter } from '../contexts/CenterContext';

const GATE_EMPTY = 'Elegí un cliente en Filtros para ver los registros.';
const GATE_SEARCH_HINT =
  'Elegí un cliente en Filtros. La búsqueda afina ese resultado (mín. 3 letras).';

const ElementosPage: React.FC = () => {
  const [elementos, setElementos] = useState<Elemento[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});
  const { clienteOptions, sucursalOptions, loadSucursalesForCliente } = useClienteSucursalFilter();
  const nextSignal = useAbortableLoad();
  const navigate = useNavigate();
  const { filtersFromUrl, pageFromUrl, writeUrlState, searchParams } = useListUrlState();
  const { currentCenter } = useCenter();
  const centerId = currentCenter?.id;

  const loadElementos = useCallback(async (filters: Record<string, any> = {}, pageNum = 1) => {
    if (!hasClienteFilter(filters)) {
      setElementos([]);
      setHasMore(false);
      setTotalCount(undefined);
      setPage(1);
      setLoading(false);
      return;
    }
    const signal = nextSignal();
    try {
      setLoading(true);
      const result = await elementoService.list(
        {
          ...toListQuery(filters),
          page: pageNum,
          pageSize: LIST_PAGE_SIZE,
        },
        signal,
      );
      setElementos(result.data);
      setHasMore(result.hasMore);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch (error) {
      if (isAbortError(error)) return;
      console.error('Error al cargar elementos:', error);
      setNotification({
        type: 'error',
        message: 'Error al cargar las instalaciones fijas',
      });
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [nextSignal]);

  useEffect(() => {
    if (!centerId) return;
    const fromUrl = { ...filtersFromUrl };
    setCurrentFilters(fromUrl);
    void loadSucursalesForCliente(fromUrl.cliente);
    if (hasClienteFilter(fromUrl)) {
      void loadElementos(fromUrl, pageFromUrl);
    } else {
      setElementos([]);
      setHasMore(false);
      setTotalCount(undefined);
      setPage(1);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reaccionar a URL/centro
  }, [centerId, searchParams]);

  const handleFilterChange = async (filters: Record<string, any>) => {
    setCurrentFilters(filters);
    writeUrlState(filters, 1);
  };

  const handlePageChange = (next: number) => {
    writeUrlState(currentFilters, next);
  };

  const handleAdd = () => {
    navigate('/elementos/nuevo');
  };

  const handleEdit = (elemento: Elemento) => {
    navigate(`/elementos/${elemento.id}/editar`);
  };

  const handleDelete = async (elemento: Elemento) => {
    if (
      window.confirm(
        '¿Estás seguro de que quieres desactivar esta instalación fija? Esta acción no se puede deshacer.',
      )
    ) {
      try {
        const result = await elementoService.delete(elemento.id);
        // Recargar la lista para mostrar el estado actualizado
        await loadElementos(currentFilters, page);
        setNotification({
          type: 'success',
          message: result.message || 'Instalación fija desactivada correctamente',
        });
      } catch (error) {
        console.error('Error al desactivar elemento:', error);
        setNotification({
          type: 'error',
          message: 'Error al desactivar la instalación fija',
        });
      }
    }
  };

  const handleView = (elemento: Elemento) => {
    navigate(`/elementos/${elemento.id}`);
  };

  const columns = [
    {
      key: 'activo',
      label: 'Activo',
      sortable: true,
      render: (value: boolean) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            value
              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          {value ? 'Sí' : 'No'}
        </span>
      ),
    },
    {
      key: 'id',
      label: 'Id',
      sortable: true,
      render: (value: number) => (
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{value}</div>
      ),
    },
    {
      key: 'sucursal',
      label: 'Sucursal',
      sortable: true,
      render: (value: any) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {value?.nombre || 'Sin sucursal'}
        </div>
      ),
    },
    {
      key: 'tipoDeElemento',
      label: 'Tipo',
      sortable: true,
      render: (value: any) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {value?.nombre || 'Sin tipo'}
        </div>
      ),
    },
    {
      key: 'ubicacion',
      label: 'Ubicación',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">
          {value || 'Sin ubicación'}
        </div>
      ),
    },
    {
      key: 'interno',
      label: 'Interno',
      sortable: true,
      render: (value: number) => (
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{value}</div>
      ),
    },
    {
      key: 'ultimoRelevamiento',
      label: 'Últ. relevamiento',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">{value || '-'}</div>
      ),
    },
  ];

  const filterConfig: FilterConfig[] = [
    {
      key: 'cliente',
      label: 'Cliente',
      type: 'select',
      options: clienteOptions,
    },
    {
      key: 'sucursal',
      label: 'Sucursal',
      type: 'select',
      dependsOn: 'cliente',
      options: sucursalOptions,
    },
  ];

  if (loading && elementos.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Instalaciones Fijas
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Elegí un cliente para listar. Sucursal y búsqueda afinan el resultado.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
        </div>
      </div>

      {viewMode === 'table' ? (
        <DataTable
          key={centerId}
          data={elementos}
          columns={columns}
          title="Lista de Instalaciones Fijas"
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          searchPlaceholder="Buscar por tipo de elemento..."
          itemsPerPage={LIST_PAGE_SIZE}
          loading={loading}
          emptyMessage={GATE_EMPTY}
          defaultShowFilters
          searchHint={GATE_SEARCH_HINT}
          filters={filterConfig}
          enableAdvancedFilters={true}
          onFilterChange={handleFilterChange}
          initialFilters={currentFilters}
          serverPagination={{
            page,
            pageSize: LIST_PAGE_SIZE,
            hasMore,
            totalCount,
            onPageChange: handlePageChange,
          }}
          actions={[
            {
              label: 'Ver',
              icon: EyeIcon,
              onClick: handleView,
              className: 'text-gray-400 hover:text-blue-500',
            },
            {
              label: 'Editar',
              icon: PencilIcon,
              onClick: handleEdit,
              className: 'text-gray-400 hover:text-yellow-500',
            },
            {
              label: 'Eliminar',
              icon: TrashIcon,
              onClick: handleDelete,
              className: 'text-gray-400 hover:text-red-500',
            },
          ]}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {elementos.map((elemento, _index) => (
              <div
                key={elemento.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow duration-200 h-full flex flex-col"
              >
                <div className="p-6 flex flex-col flex-1 min-h-0">
                  {/* Header */}
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white break-words">
                      ID: {elemento.id} | Interno: {elemento.interno}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {elemento.sucursal?.nombre || 'Sin sucursal'}
                    </p>
                  </div>

                  {/* Details */}
                  <div className="space-y-3 flex-1">
                    {elemento.tipoDeElemento && (
                      <div className="text-sm">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">Tipo:</div>
                        <div className="text-gray-600 dark:text-gray-300">
                          {elemento.tipoDeElemento.nombre}
                        </div>
                      </div>
                    )}

                    {elemento.ubicacion && (
                      <div className="flex items-center text-sm">
                        <MapPinIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
                        <span className="text-gray-600 dark:text-gray-300 truncate">
                          {elemento.ubicacion}
                        </span>
                      </div>
                    )}

                    {elemento.ultimoRelevamiento && (
                      <div className="text-sm">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Últ. relevamiento:
                        </div>
                        <div className="text-gray-600 dark:text-gray-300">
                          {elemento.ultimoRelevamiento}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-auto flex items-center justify-between h-12 shrink-0 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        elemento.activo
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}
                    >
                      {elemento.activo ? 'Activo' : 'Inactivo'}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleView(elemento)}
                        className="p-2 text-gray-400 hover:text-blue-500 transition-colors duration-200"
                        title="Ver detalles"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(elemento)}
                        className="p-2 text-gray-400 hover:text-yellow-500 transition-colors duration-200"
                        title="Editar"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(elemento)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors duration-200"
                        title="Eliminar"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <ServerPageBar
            page={page}
            pageSize={LIST_PAGE_SIZE}
            hasMore={hasMore}
            rowCount={elementos.length}
            totalCount={totalCount}
            loading={loading}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default ElementosPage;
